import { isApolloError } from '@apollo/client';
import { useCallback, useRef } from 'react';
import { tracking } from 'App/Tracking';
import { Errors } from 'Shared/Errors';
import {
  ACCOUNT_CREATION_STEP_NAMES,
  ConfirmUserAccountError,
  SubRoutes,
  TrackedAccountCreationFailureReason,
} from '../constants';
import { AccountCreationTracker } from '../types';

const getFailureReason = (
  reasonOrError: TrackedAccountCreationFailureReason | Error,
): TrackedAccountCreationFailureReason => {
  if (!(reasonOrError instanceof Error)) return reasonOrError;

  if (!isApolloError(reasonOrError)) {
    return TrackedAccountCreationFailureReason.Unknown;
  }

  switch (reasonOrError.graphQLErrors[0]?.name) {
    case ConfirmUserAccountError.OrganisationInactive:
      return TrackedAccountCreationFailureReason.OrganisationInactive;
    case Errors.RateLimitError:
      return TrackedAccountCreationFailureReason.RateLimited;
    default:
      return TrackedAccountCreationFailureReason.Unknown;
  }
};

/**
 * Unlike its verification counterpart this carries no per-user props, so it is
 * a module singleton rather than a factory: users reach account creation on a
 * cold page load from an emailed registration token, potentially on a different
 * device, so no entry point or registration method is knowable client side.
 */
export const accountCreationTracker: AccountCreationTracker = {
  stepViewed: stepName =>
    tracking.track('account-creation-step-viewed', { stepName }),

  stepCompleted: stepName =>
    tracking.track('account-creation-step-completed', { stepName }),

  successful: () => tracking.track('account-creation-successful'),

  failed: reasonOrError =>
    tracking.track('account-creation-failed', {
      reason: getFailureReason(reasonOrError),
    }),
};

export const useAccountCreationTracking = (): {
  onStepViewed(route: SubRoutes): void;
  onStepCompleted(route: SubRoutes): void;
} => {
  const lastViewedStep = useRef<SubRoutes>();

  const onStepViewed = useCallback((route: SubRoutes) => {
    const stepName = ACCOUNT_CREATION_STEP_NAMES[route];

    if (!stepName || lastViewedStep.current === route) return;

    lastViewedStep.current = route;
    accountCreationTracker.stepViewed(stepName);
  }, []);

  const onStepCompleted = useCallback((route: SubRoutes) => {
    const stepName = ACCOUNT_CREATION_STEP_NAMES[route];

    if (stepName) accountCreationTracker.stepCompleted(stepName);
  }, []);

  return { onStepViewed, onStepCompleted };
};
